/**
 * The Codex: the bestiary and the book of works, read straight out of
 * `src/data`.
 *
 * Every unit, building, power and relic in the catalogue already carries its
 * own name, flavour and — for powers and relics — exact rules text, because
 * the tooltips were written information-first from the start. This screen is
 * therefore *layout*: four tabs, a rail of entries, and one sheet, built as
 * HTML strings for `menu.ts` to place the way the kingdom screen's sheet is.
 * Nothing here computes a game number; every figure is quoted from the def,
 * so the codex can never disagree with the game. The one editorial decision
 * is the ability line: only the three wired invader signatures are described,
 * because printing a name the simulation does not read would be a promise.
 */

import { UNIT_TYPES, unitWage, type UnitType } from '../data/units.ts';
import { BUILDING_TYPES, type BuildingType } from '../data/buildings.ts';
import { POWERS, type PowerDef } from '../data/powers.ts';
import { RELICS, type RelicDef } from '../data/relics.ts';
import { FACTIONS } from '../data/factions.ts';
import {
  ArmourType,
  DamageType,
  RESOURCE_KEYS,
  RESOURCE_LABELS,
  UF,
  type ResourceBundle,
} from '../sim/types.ts';

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}

export type CodexTab = 'soldiers' | 'buildings' | 'powers' | 'relics';

export const CODEX_TABS: Array<{ id: CodexTab; label: string }> = [
  { id: 'soldiers', label: 'Soldiers' },
  { id: 'buildings', label: 'Buildings' },
  { id: 'powers', label: 'Powers' },
  { id: 'relics', label: 'Relics' },
];

export interface CodexRow {
  id: string;
  label: string;
  sub: string;
  /** Rail group heading this row sits under. */
  group: string;
}

const DAMAGE_NAMES: Record<number, string> = {
  [DamageType.Slash]: 'Slash',
  [DamageType.Pierce]: 'Pierce',
  [DamageType.Blunt]: 'Blunt',
  [DamageType.Arrow]: 'Arrow',
  [DamageType.Siege]: 'Siege',
  [DamageType.Arcane]: 'Arcane',
  [DamageType.Fire]: 'Fire',
  [DamageType.Poison]: 'Poison',
};

const ARMOUR_NAMES: Record<number, string> = {
  [ArmourType.Unarmoured]: 'Unarmoured',
  [ArmourType.Leather]: 'Leather',
  [ArmourType.Mail]: 'Mail',
  [ArmourType.Plate]: 'Plate',
  [ArmourType.Shielded]: 'Shielded',
  [ArmourType.Structure]: 'Structure',
  [ArmourType.Monstrous]: 'Monstrous',
};

/**
 * The wired signatures only. An ability id the simulation does not read is a
 * word, and the codex does not print words as mechanics.
 */
const ABILITY_NOTES: Record<string, string> = {
  burst: 'Bursts on death: a puff of rot that poisons everyone beside the corpse, its own side included.',
  burrow: 'Tunnels: crosses walls, gates and towers as if they were ground, and never stops to attack one.',
  madness: 'The song: a soldier struck by this turns on his own side for a few seconds.',
};

function costLine(cost: Partial<ResourceBundle>): string {
  const parts: string[] = [];
  for (const k of RESOURCE_KEYS) {
    const v = cost[k] ?? 0;
    if (v > 0) parts.push(`${Math.round(v * 100) / 100} ${RESOURCE_LABELS[k]}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'nothing';
}

function num(label: string, value: string): string {
  return `<div class="num"><span>${esc(label)}</span><b>${esc(value)}</b></div>`;
}

// ── Soldiers ────────────────────────────────────────────────────────────────

function factionLabel(id: string): string {
  const f = FACTIONS.find((x) => x.id === id);
  return f ? f.name : id === 'common' ? 'Every banner' : id;
}

export function codexRail(tab: CodexTab): CodexRow[] {
  if (tab === 'soldiers') {
    return UNIT_TYPES.filter((t) => (t.flags & UF.Villager) === 0 && t.faction !== 'hero').map((t) => ({
      id: t.id,
      label: t.name,
      sub: `Age ${t.tier} · threat ${t.threat}`,
      group: factionLabel(t.faction),
    }));
  }
  if (tab === 'buildings') {
    return BUILDING_TYPES.map((b) => ({
      id: b.id,
      label: b.name,
      sub: `Age ${b.tier}`,
      group: `Age ${b.tier}`,
    }));
  }
  if (tab === 'powers') {
    return POWERS.map((p) => ({
      id: p.id,
      label: p.name,
      sub: `${p.cost} Will`,
      group: `Age ${p.age ?? 1}`,
    }));
  }
  return [...RELICS]
    .sort((a, b) => a.rarity - b.rarity || a.name.localeCompare(b.name))
    .map((r) => ({
      id: r.id,
      label: r.name,
      sub: `wave ${r.minWave}+`,
      group: r.rarity >= 3 ? 'Rare' : r.rarity === 2 ? 'Uncommon' : 'Common',
    }));
}

/**
 * What a soldier *is*, as opposed to what its numbers are.
 *
 * The sheet printed six statistics and never once said that a thing flies. That
 * was survivable while `UF.Flying` was a bit no unit in the catalogue set; it
 * stopped being survivable the moment there were sixteen fliers, because the
 * one fact that decides whether your walls are worth anything against a company
 * was the one fact the codex would not tell you.
 *
 * Every line here is quoted from the mechanism rather than from the flavour —
 * the multipliers are the ones in `systems/combat.ts` — because a codex that
 * rounds "1.9× against horse" up to "good against cavalry" is a codex the
 * player has to go and test anyway. Where a trait is conditional, the condition
 * is the interesting half and is what is printed: a shield only eats what it is
 * facing, and a charge only lands on somebody who is not braced.
 *
 * Ordered by how much it changes the answer, not by the bit order in `UF`.
 */
export const TRAIT_NOTES: [number, string][] = [
  [UF.Flying, 'Flies. Walls, gates, water and broken ground are simply not there — though most towers can still reach it.'],
  [UF.Siege, 'A siege engine. It has come for the stone, not for the men on it.'],
  [UF.Sapper, 'Sapper. Carries ladders, and can take men off an intact wall.'],
  [UF.Cavalry, 'Horse. The charge is a burst worth 2.4× that has to be won back by disengaging and coming round again.'],
  [UF.Spear, 'Spears. 1.9× against horse, and a spearman who has stopped moving refuses a charge outright.'],
  [UF.Shielded, 'Shielded — but only against what it is facing. Shot from the flank lands in full.'],
  [UF.Large, 'Large. Cannot be shoved, and siege weapons lose nothing against it.'],
  [UF.SpellEater, 'Eats spells. Arcane and fire land at 45%.'],
  [UF.Champion, 'A champion. Fearless, and cannot be knocked back.'],
  [UF.Undead, 'Risen. It does not break and it does not run.'],
  [UF.Summoned, 'Summoned. Fearless, and leaves nothing behind worth raising.'],
  [UF.Caster, 'A caster. Reach it early.'],
];

function traitBand(t: UnitType): string {
  const lines = TRAIT_NOTES.filter(([bit]) => (t.flags & bit) !== 0).map(([, text]) => text);
  if (lines.length === 0) return '';
  return (
    `<div class="band"><div class="cap">What it is</div>` +
    lines.map((l) => `<p>${esc(l)}</p>`).join('') +
    `</div>`
  );
}

function soldierSheet(t: UnitType): string {
  const melee = t.range <= 0.8;
  const ability = t.ability ? ABILITY_NOTES[t.ability] : undefined;
  const wage = unitWage(t);
  return (
    `<div class="head"><div>
       <h2>${esc(t.name)}</h2>
       <div class="who">${esc(factionLabel(t.faction))} · Age ${t.tier}</div>
       <div class="fl">${esc(t.flavour)}</div>
     </div></div>` +
    `<div class="cols" style="grid-template-columns:1fr 1fr">
       <div class="col"><div class="cap">In the field</div>
         ${num('Health', `${t.hp}`)}
         ${num('Damage', `${t.damage} ${DAMAGE_NAMES[t.dmgType] ?? ''}${t.splash > 0 ? `, splash ${t.splash}` : ''}`)}
         ${num(melee ? 'Reach' : 'Range', melee ? 'melee' : `${t.range} tiles`)}
         ${num('Swings', `${t.attacksPerSec}/s`)}
         ${num('Armour', ARMOUR_NAMES[t.armour] ?? String(t.armour))}
         ${num('Speed', `${t.speed} tiles/s`)}
         ${t.ammo > 0 ? num('Quiver', `${t.ammo} shots`) : ''}
       </div>
       <div class="col"><div class="cap">On the books</div>
         ${num('Company', `${t.companySize} models`)}
         ${num('One model costs', costLine(t.cost))}
         ${wage > 0 ? num('Wage', `${wage.toFixed(2)} gold/min per model`) : num('Wage', 'unpaid')}
         ${num('Threat', `${t.threat}`)}
       </div>
     </div>` +
    traitBand(t) +
    (ability ? `<div class="band"><div class="cap">Signature</div><p>${esc(ability)}</p></div>` : '')
  );
}

function buildingSheet(b: BuildingType): string {
  const makes = costLine(b.produces);
  const eats = costLine(b.consumes);
  return (
    `<div class="head"><div>
       <h2>${esc(b.name)}</h2>
       <div class="who">Age ${b.tier} · ${b.w}×${b.h}</div>
       <div class="fl">${esc(b.flavour)}</div>
     </div></div>` +
    `<div class="cols" style="grid-template-columns:1fr 1fr">
       <div class="col"><div class="cap">The works</div>
         ${b.workers > 0 ? num('Hands', `${b.workers}`) : ''}
         ${makes !== 'nothing' ? num('Makes', `${makes} /s at full staffing`) : ''}
         ${eats !== 'nothing' ? num('Eats', `${eats} /s`) : ''}
         ${b.popCap > 0 ? num('Beds', `${b.popCap}`) : ''}
         ${b.storage > 0 ? num('Storage', `+${b.storage}`) : ''}
         ${b.mana > 0 ? num('Pool', `+${b.mana}`) : ''}
         ${b.toll > 0 ? num('The toll', `a head per ${b.toll}s of full-rate work`) : ''}
       </div>
       <div class="col"><div class="cap">The bill</div>
         ${num('Costs', costLine(b.cost))}
         ${num('Health', `${b.hp}`)}
         ${num('Raised in', `${b.buildTime} builder-seconds`)}
       </div>
     </div>`
  );
}

function powerSheet(p: PowerDef): string {
  return (
    `<div class="head"><div>
       <h2>${esc(p.glyph)} ${esc(p.name)}</h2>
       <div class="who">Age ${p.age ?? 1}${p.friendlyFire ? ' · spares nobody' : ''}</div>
       <div class="fl">${esc(p.flavour)}</div>
     </div></div>` +
    `<div class="band"><div class="cap">The rules, exactly</div><p>${esc(p.rules)}</p></div>` +
    `<div class="cols" style="grid-template-columns:1fr 1fr 1fr">
       <div class="col">${num('Will', `${p.cost}`)}</div>
       <div class="col">${num('Cooldown', `${p.cooldown}s`)}</div>
       <div class="col">${p.radius > 0 ? num('Radius', `${p.radius} tiles`) : num('Radius', '—')}</div>
     </div>`
  );
}

function relicSheet(r: RelicDef): string {
  const rarity = r.rarity >= 3 ? 'Rare' : r.rarity === 2 ? 'Uncommon' : 'Common';
  return (
    `<div class="head"><div>
       <h2>${esc(r.name)}</h2>
       <div class="who">${rarity} · offered from wave ${r.minWave}</div>
       <div class="fl">${esc(r.flavour)}</div>
     </div></div>` +
    `<div class="band"><div class="cap">The rules, exactly</div><p>${esc(r.rules)}</p></div>`
  );
}

/** The sheet for one entry, or a gentle nothing when the id is unknown. */
export function codexSheet(tab: CodexTab, id: string): string {
  if (tab === 'soldiers') {
    const t = UNIT_TYPES.find((x) => x.id === id);
    return t ? soldierSheet(t) : '';
  }
  if (tab === 'buildings') {
    const b = BUILDING_TYPES.find((x) => x.id === id);
    return b ? buildingSheet(b) : '';
  }
  if (tab === 'powers') {
    const p = POWERS.find((x) => x.id === id);
    return p ? powerSheet(p) : '';
  }
  const r = RELICS.find((x) => x.id === id);
  return r ? relicSheet(r) : '';
}

/** The first entry of a tab — what the screen opens on. */
export function codexFirst(tab: CodexTab): string {
  const rows = codexRail(tab);
  return rows.length > 0 ? rows[0].id : '';
}
